/**
 * Internationalization (i18n) Service
 *
 * Lightweight i18n system for the AppBouncer SDK.
 * Supports language switching, browser detection, and type-safe translations.
 *
 * @example
 * ```typescript
 * import { i18n, t } from './i18n';
 *
 * // Set language
 * i18n.setLanguage('es');
 *
 * // Get translations
 * t('face.noFace'); // "Posiciona tu rostro en el marco"
 * t('liveness.blink.title'); // "Parpadea"
 * ```
 */

import type { Language, Translations, PartialTranslations } from './types';
import { en } from './translations/en';
import { es } from './translations/es';
import { fr } from './translations/fr';
import { de } from './translations/de';
import { pt } from './translations/pt';
import { ar } from './translations/ar';
import { zh } from './translations/zh';
import { ja } from './translations/ja';
import { hi } from './translations/hi';
import { ru } from './translations/ru';
import { fa } from './translations/fa';

// ============================================================
// Translation Registry
// ============================================================

/**
 * All available translations.
 * Add new languages here when adding translation files.
 */
const translations: Record<Language, Translations> = {
  en,
  es,
  fr,
  de,
  pt,
  ar,
  zh,
  ja,
  hi,
  ru,
  fa,
};

// ============================================================
// I18n Service Class
// ============================================================

/**
 * Listener callback for language changes.
 */
export type LanguageChangeListener = (language: Language) => void;

/**
 * I18n service class.
 * Manages language state and translation lookups.
 */
export class I18nService {
  private currentLanguage: Language = 'en';
  private listeners: Set<LanguageChangeListener> = new Set();
  private instanceTranslations: Record<Language, Translations>;

  constructor() {
    // Create a shallow copy of translations for this instance
    // Deep copies are only made when registerTranslation is called
    this.instanceTranslations = { ...translations };
  }

  /**
   * Get the current language.
   */
  getLanguage(): Language {
    return this.currentLanguage;
  }

  /**
   * Set the current language.
   *
   * @param language - Language code to set
   * @returns true if language was changed, false if already set or unsupported
   */
  setLanguage(language: Language): boolean {
    if (language === this.currentLanguage) {
      return false;
    }

    if (!translations[language]) {
      console.warn(`[i18n] Language "${language}" not found, falling back to English`);
      language = 'en';
    }

    this.currentLanguage = language;
    this.notifyListeners();
    return true;
  }

  /**
   * Detect and set language from browser settings.
   * Returns the detected language.
   */
  detectBrowserLanguage(): Language {
    if (typeof navigator === 'undefined') {
      return 'en';
    }

    // Get browser language (e.g., "en-US" -> "en")
    const browserLang = navigator.language?.split('-')[0] as Language;

    if (browserLang && translations[browserLang]) {
      this.setLanguage(browserLang);
      return browserLang;
    }

    return 'en';
  }

  /**
   * Get the current translations object.
   */
  getTranslations(): Translations {
    return this.instanceTranslations[this.currentLanguage] || this.instanceTranslations.en;
  }

  /**
   * Get a translation by dot-notation path.
   *
   * @param path - Dot-separated path (e.g., 'face.noFace', 'liveness.blink.title')
   * @param fallback - Optional fallback if path not found
   * @returns The translation string or fallback
   *
   * @example
   * ```typescript
   * i18n.translate('face.noFace'); // "Position your face in the frame"
   * i18n.translate('liveness.blink.hint'); // "Slowly blink once"
   * ```
   */
  translate(path: string, fallback?: string): string {
    const keys = path.split('.');
    let value: unknown = this.getTranslations();

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        // Try English fallback
        value = this.getNestedValue(this.instanceTranslations.en, keys);
        break;
      }
    }

    if (typeof value === 'string') {
      return value;
    }

    return fallback ?? path;
  }

  /**
   * Shorthand alias for translate().
   * @see translate
   */
  t(path: string, fallback?: string): string {
    return this.translate(path, fallback);
  }

  /**
   * Get nested value from object by key array.
   */
  private getNestedValue(obj: unknown, keys: string[]): unknown {
    let value = obj;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }
    return value;
  }

  /**
   * Get all available languages.
   */
  getAvailableLanguages(): Array<{ code: Language; name: string }> {
    return Object.entries(this.instanceTranslations)
      .filter(([, t]) => t !== this.instanceTranslations.en || t.language === 'en')
      .map(([code, t]) => ({
        code: code as Language,
        name: t.languageName,
      }));
  }

  /**
   * Check if a language is supported.
   */
  isSupported(language: string): language is Language {
    return language in this.instanceTranslations;
  }

  /**
   * Register a custom translation or extend existing ones.
   * Note: This modifies only this instance's translations, not the shared registry.
   *
   * @param language - Language code
   * @param customTranslations - Partial translations to merge
   */
  registerTranslation(language: Language, customTranslations: PartialTranslations): void {
    const base = this.instanceTranslations[language] || this.instanceTranslations.en;
    this.instanceTranslations[language] = this.deepMerge(base, customTranslations) as Translations;
  }

  /**
   * Add a language change listener.
   */
  onLanguageChange(listener: LanguageChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Translate all elements in the DOM with data-i18n attribute.
   * Supports text content and various HTML attributes.
   *
   * Usage in HTML:
   * - data-i18n="face.noFace" - Sets textContent
   * - data-i18n-placeholder="ui.searchPlaceholder" - Sets placeholder attribute
   * - data-i18n-title="ui.helpTitle" - Sets title attribute
   * - data-i18n-aria-label="ui.buttonLabel" - Sets aria-label attribute
   *
   * @param root - Root element to search within (defaults to document.body)
   */
  translateDOM(root?: Element): void {
    if (typeof document === 'undefined') return;

    const container = root || document.body;

    // Translate text content
    const textElements = container.querySelectorAll('[data-i18n]');
    textElements.forEach((el) => {
      const key = el.getAttribute('data-i18n');
      if (key) {
        el.textContent = this.translate(key);
      }
    });

    // Translate HTML content (for translations containing trusted markup like links).
    // Safe: translation strings are developer-authored in i18n files, not user input.
    const htmlElements = container.querySelectorAll('[data-i18n-html]');
    htmlElements.forEach((el) => {
      const key = el.getAttribute('data-i18n-html');
      if (key) {
        el.innerHTML = this.translate(key);
      }
    });

    // Translate placeholder attributes
    const placeholderElements = container.querySelectorAll('[data-i18n-placeholder]');
    placeholderElements.forEach((el) => {
      const key = el.getAttribute('data-i18n-placeholder');
      if (key) {
        el.setAttribute('placeholder', this.translate(key));
      }
    });

    // Translate title attributes
    const titleElements = container.querySelectorAll('[data-i18n-title]');
    titleElements.forEach((el) => {
      const key = el.getAttribute('data-i18n-title');
      if (key) {
        el.setAttribute('title', this.translate(key));
      }
    });

    // Translate aria-label attributes
    const ariaElements = container.querySelectorAll('[data-i18n-aria-label]');
    ariaElements.forEach((el) => {
      const key = el.getAttribute('data-i18n-aria-label');
      if (key) {
        el.setAttribute('aria-label', this.translate(key));
      }
    });
  }

  /**
   * Enable auto-translation on language change.
   * Automatically calls translateDOM when language changes.
   *
   * @param root - Root element to translate (defaults to document.body)
   * @returns Unsubscribe function
   */
  enableAutoTranslate(root?: Element): () => void {
    // Translate immediately
    this.translateDOM(root);

    // Re-translate on language change
    return this.onLanguageChange(() => {
      this.translateDOM(root);
    });
  }

  /**
   * Notify all listeners of language change.
   */
  private notifyListeners(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.currentLanguage);
      } catch (error) {
        console.error('[i18n] Listener error:', error);
      }
    }
  }

  /**
   * Deep merge two objects.
   */
  private deepMerge(target: unknown, source: unknown): unknown {
    if (!source || typeof source !== 'object') {
      return target;
    }

    if (!target || typeof target !== 'object') {
      return source;
    }

    const result = { ...target } as Record<string, unknown>;

    for (const key of Object.keys(source as object)) {
      const sourceValue = (source as Record<string, unknown>)[key];
      const targetValue = (target as Record<string, unknown>)[key];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        result[key] = this.deepMerge(targetValue, sourceValue);
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue;
      }
    }

    return result;
  }
}

// ============================================================
// Singleton Instance and Helper Functions
// ============================================================

/**
 * Singleton i18n service instance.
 */
export const i18n = new I18nService();

/**
 * Shorthand for i18n.translate().
 *
 * @param path - Dot-separated translation path
 * @param fallback - Optional fallback value
 * @returns The translation string
 *
 * @example
 * ```typescript
 * t('face.noFace'); // "Position your face in the frame"
 * t('errors.networkError'); // "Network error. Please check your connection."
 * ```
 */
export function t(path: string, fallback?: string): string {
  return i18n.translate(path, fallback);
}

/**
 * Get face message by key.
 * Typed helper for face positioning messages.
 *
 * @param key - Face message key
 * @returns The face message
 */
export function getFaceText(
  key: 'noFace' | 'tooFar' | 'tooClose' | 'offCenter' | 'poorLighting' | 'good' | 'holdStill'
): string {
  return i18n.getTranslations().face[key];
}

/**
 * Get liveness action text.
 * Typed helper for liveness action messages.
 *
 * @param action - Liveness action key
 * @returns Object with title and hint
 */
export function getLivenessText(
  action:
    | 'blink'
    | 'smile'
    | 'lookLeft'
    | 'lookRight'
    | 'openMouth'
    | 'lookUp'
    | 'lookDown'
    | 'raiseBrows'
): { title: string; hint: string } {
  return i18n.getTranslations().liveness[action];
}

/**
 * Get error message by key.
 * Typed helper for error messages.
 *
 * @param key - Error message key
 * @returns The error message
 */
export function getErrorText(
  key:
    | 'cameraNotFound'
    | 'cameraPermissionDenied'
    | 'networkError'
    | 'timeout'
    | 'invalidApiKey'
    | 'sessionExpired'
    | 'verificationFailed'
    | 'unknownError'
): string {
  return i18n.getTranslations().errors[key];
}

/**
 * Get UI text by key.
 * Typed helper for general UI strings.
 *
 * @param key - UI message key
 * @returns The UI message
 */
export function getUIText(
  key:
    | 'continue'
    | 'retry'
    | 'startOver'
    | 'cancel'
    | 'success'
    | 'failure'
    | 'loading'
    | 'error'
    | 'permissionRequired'
    | 'cameraPermission'
): string {
  return i18n.getTranslations().ui[key];
}
